import {
  ChangeDetectionStrategy,
  Component,
  Injector,
  afterNextRender,
  computed,
  inject,
  linkedSignal,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, NavigationSkipped, Router, RouterOutlet } from '@angular/router';
import { filter, map } from 'rxjs';

import { AuthFacade } from '../../authentication/data-access/facades/auth.facade';
import { BreadcrumbService } from '../../core/services/breadcrumb.service';
import { MenuFacade } from '../../core/menu/data-access/facades/menu.facade';
import { MenuItem } from '../../core/menu/domain/menu-item.model';
import { NotificationBellContainerComponent } from '../../notifications/features/notification-bell/notification-bell-container.component';
import { SidebarStateService } from '../../core/services/sidebar-state.service';
import { ViewportService } from '../../core/services/viewport.service';
import { HeaderComponent } from '../../shared/ui/header/header.component';
import { SidebarComponent } from '../../shared/ui/sidebar/sidebar.component';
import { SidebarUser } from '../../shared/ui/sidebar/sidebar-user.model';
import { fuzzyMatch } from '../../shared/utils/fuzzy-search.util';

@Component({
  selector: 'app-main-layout',
  templateUrl: './main-layout.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterOutlet, SidebarComponent, HeaderComponent, NotificationBellContainerComponent],
})
export class MainLayoutComponent {
  private readonly authFacade = inject(AuthFacade);
  private readonly menuFacade = inject(MenuFacade);
  private readonly sidebarState = inject(SidebarStateService);
  private readonly router = inject(Router);
  private readonly breadcrumbService = inject(BreadcrumbService);

  private readonly injector = inject(Injector);
  private readonly viewport = inject(ViewportService).viewport;

  private readonly header = viewChild.required(HeaderComponent);

  protected readonly user = this.authFacade.user;

  protected readonly searchTerm = signal('');

  /**
   * Modo del shell por rango de pantalla (US-097, E-011 RN-1):
   * - móvil (<768): sin sidebar en línea; panel superpuesto desde `app-header`.
   * - mediana (768–1279): arranca colapsado; el toggle no se guarda.
   * - grande (≥1280): preferencia guardada en `SidebarStateService` (sin cambios).
   */
  protected readonly isMobile = computed(() => this.viewport() === 'mobile');

  /** Preferencia persistida; solo aplica en pantalla grande. */
  private readonly storedCollapsed = signal(this.sidebarState.getCollapsed());

  /** Estado de sesión en mediana: vuelve a colapsado cada vez que se cambia de rango. */
  private readonly mediumCollapsed = linkedSignal({ source: this.viewport, computation: () => true });

  protected readonly collapsed = computed(() =>
    this.viewport() === 'medium' ? this.mediumCollapsed() : this.storedCollapsed(),
  );

  /** Panel móvil abierto. Se cierra solo al cambiar de rango (p. ej. rotar a ≥768). */
  protected readonly mobileMenuOpen = linkedSignal({ source: this.viewport, computation: () => false });

  private readonly navigationEnd$ = this.router.events.pipe(
    filter((event): event is NavigationEnd => event instanceof NavigationEnd),
  );

  protected readonly activeUrl = toSignal(this.navigationEnd$.pipe(map((event) => event.urlAfterRedirects)), {
    initialValue: this.router.url,
  });

  constructor() {
    // Elegir una ruta en el panel móvil lo cierra: llegar a una pantalla cuesta un solo toque extra (RN-6).
    // `NavigationSkipped` cubre elegir la ruta en la que ya se está (el router no emite `NavigationEnd`).
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd || event instanceof NavigationSkipped),
        takeUntilDestroyed(),
      )
      .subscribe(() => this.closeMobileMenu());
  }

  protected readonly filteredMenu = computed(() =>
    this.filterMenu(this.menuFacade.items(), this.searchTerm().trim()),
  );

  /**
   * Título de la title bar (`app-header`): label de la ruta hoja activa
   * (`data.breadcrumb`, ya definido en cada `*.routes.ts`). `BreadcrumbService`
   * ya resuelve ese árbol; tomamos el último item, que es la ruta más
   * profunda actualmente activa.
   */
  protected readonly headerTitle = computed(() => {
    const items = this.breadcrumbService.items();
    return items.length ? items[items.length - 1].label : '';
  });

  /**
   * Badge de la title bar: nombre real del módulo (no un departamento
   * inventado), tomado del menú según el primer segmento de la URL activa.
   * El menú viene del backend (`MenuFacade.items`), así que el nombre
   * siempre corresponde a un módulo real habilitado para el usuario.
   */
  protected readonly headerBadge = computed(() => {
    const segment = this.firstUrlSegment(this.activeUrl());
    if (!segment) return '';

    const module = this.menuFacade.items().find((item) => item.route === segment);
    return module?.name ?? '';
  });

  /**
   * Vista reducida del usuario para el footer de sesión del sidebar
   * (contrato `SidebarUser`: solo `name`/`email`, sin campos supuestos como
   * `connected`).
   */
  protected readonly sidebarUser = computed<SidebarUser | null>(() => {
    const current = this.user();
    return current ? { name: current.name, email: current.email } : null;
  });

  protected onToggleCollapse(): void {
    if (this.viewport() === 'medium') {
      this.mediumCollapsed.update((collapsed) => !collapsed);
      return;
    }

    const next = !this.storedCollapsed();
    this.storedCollapsed.set(next);
    this.sidebarState.setCollapsed(next);
  }

  protected openMobileMenu(): void {
    this.mobileMenuOpen.set(true);
  }

  /** Cierra el panel móvil y devuelve el foco al botón de menú que lo abrió. */
  protected closeMobileMenu(): void {
    if (!this.mobileMenuOpen()) return;

    this.mobileMenuOpen.set(false);
    afterNextRender(() => this.header().focusMenuButton(), { injector: this.injector });
  }

  protected onLogout(): void {
    // logout() limpia el estado de forma síncrona; la revocación en backend es best-effort y no bloquea la navegación.
    void this.authFacade.logout();
    void this.router.navigateByUrl('/auth/ingresar');
  }

  private firstUrlSegment(url: string): string {
    return url.replace(/^\/+/, '').split('/')[0] ?? '';
  }

  private filterMenu(items: MenuItem[], term: string): MenuItem[] {
    if (!term) return items;

    return items.reduce<MenuItem[]>((acc, item) => {
      const matchesSelf = fuzzyMatch(item.name, term);
      const filteredChildren = item.subModules ? this.filterMenu(item.subModules, term) : undefined;

      if (matchesSelf || (filteredChildren && filteredChildren.length > 0)) {
        acc.push({ ...item, subModules: filteredChildren });
      }

      return acc;
    }, []);
  }
}
